import { useState } from "react";
import { DateInput } from "./DateInput";
import { Link } from "react-router-dom";
import { fieldValueText, useWorkspace, type FieldDef } from "../lib/workspace";
import { useOrg } from "../lib/session";
import { ErrorText } from "./ui";

type Values = Record<string, unknown>;

// CustomFields shows a contact's or deal's custom details and lets people
// who can edit the record change them. Only changed values are sent.
export function CustomFields({ entity, values, canEdit, onSave }: {
  entity: "contact" | "deal";
  values: Values | undefined;
  canEdit: boolean;
  onSave: (custom: Values) => Promise<unknown>;
}) {
  const ws = useWorkspace();
  const { orgId, allows } = useOrg();
  const defs = ws.data?.fields[entity] ?? [];
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Values>({});
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const current = values ?? {};
  if (defs.length === 0) {
    if (!allows("workspace")) return null; // admins get a pointer to set fields up
    return (
      <section className="card">
        <h2>More details</h2>
        <p className="muted small">Add your own fields, like "Budget" or "Sector", in <Link to={`/o/${orgId}/settings/workspace`}>Fields & words</Link>.</p>
      </section>
    );
  }

  const start = () => { setDraft({ ...current }); setError(null); setEditing(true); };
  const save = async () => {
    const changed: Values = {};
    for (const d of defs) {
      const before = JSON.stringify(current[d.key] ?? null);
      const after = JSON.stringify(draft[d.key] ?? null);
      if (before !== after) changed[d.key] = emptyToNull(draft[d.key]);
    }
    if (Object.keys(changed).length === 0) { setEditing(false); return; }
    setSaving(true);
    setError(null);
    try {
      await onSave(changed);
      setEditing(false);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="card">
      <div className="inline" style={{ justifyContent: "space-between" }}>
        <h2 style={{ margin: 0 }}>More details</h2>
        {canEdit && !editing && <button className="secondary" onClick={start}>Edit details</button>}
      </div>
      {!editing ? (
        <dl className="details">
          {defs.flatMap((d) => [
            <dt key={d.key + "-t"}>{d.label_en}</dt>,
            <dd key={d.key + "-d"}>{d.type === "url" && current[d.key] ? <a href={String(current[d.key])} target="_blank" rel="noopener noreferrer">{String(current[d.key])}</a> : fieldValueText(d, current[d.key])}</dd>,
          ])}
        </dl>
      ) : (
        <div className="form custom-fields-form">
          {defs.map((d) => (
            <FieldInput key={d.key} def={d} value={draft[d.key]} onChange={(v) => setDraft((x) => ({ ...x, [d.key]: v }))} />
          ))}
          <ErrorText error={error} />
          <div className="inline">
            <button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save details"}</button>
            <button className="secondary" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}

function emptyToNull(v: unknown) {
  if (v === "" || v === undefined || (Array.isArray(v) && v.length === 0)) return null;
  return v;
}

export function FieldInput({ def: d, value, onChange }: { def: FieldDef; value: unknown; onChange: (v: unknown) => void }) {
  const id = `cf-${d.key}`;
  const label = <label htmlFor={id}>{d.label_en}{d.ai_fillable && <span className="tag ai-tag" title="The AI assistant can fill this in from conversations">AI</span>}</label>;
  switch (d.type) {
    case "long_text":
      return <div className="field">{label}<textarea id={id} rows={3} maxLength={5000} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} /></div>;
    case "number":
      return <div className="field">{label}<input id={id} type="number" step="any" value={value === undefined || value === null ? "" : String(value)} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} /></div>;
    case "date":
      return <div className="field">{label}<DateInput id={id} value={String(value ?? "")} onChange={(v) => onChange(v)} /></div>;
    case "checkbox":
      return <label className="inline small field-check"><input id={id} type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} /> {d.label_en}</label>;
    case "select":
      return (
        <div className="field">{label}
          <select id={id} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}>
            <option value="">—</option>
            {d.options.map((o) => <option key={o.value} value={o.value}>{o.label_en}</option>)}
          </select>
        </div>
      );
    case "multi_select": {
      const set = new Set(Array.isArray(value) ? (value as string[]) : []);
      return (
        <fieldset className="field choices">
          <legend>{d.label_en}{d.ai_fillable && <span className="tag ai-tag">AI</span>}</legend>
          {d.options.map((o) => (
            <label key={o.value} className="chip">
              <input type="checkbox" checked={set.has(o.value)} onChange={(e) => {
                const next = new Set(set);
                if (e.target.checked) next.add(o.value); else next.delete(o.value);
                onChange(d.options.map((x) => x.value).filter((v) => next.has(v)));
              }} />
              {o.label_en}
            </label>
          ))}
        </fieldset>
      );
    }
    default: {
      const type = d.type === "email" ? "email" : d.type === "url" ? "url" : d.type === "phone" ? "tel" : "text";
      return <div className="field">{label}<input id={id} type={type === "url" ? "text" : type} maxLength={500} placeholder={d.type === "phone" ? "+973 3300 0000" : d.type === "url" ? "example.com" : undefined} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)} /></div>;
    }
  }
}
